import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import LineTabs, { TabType } from 'app.feature/academy/component/tab/LineTabs';
import CommunityBoard from 'app.component/communityBoard/CommunityBoard';
import CbtRoundTable from 'app.feature/academy/cbt.detail/component/CbtRoundTable';
import CbtDescription from 'app.feature/academy/cbt.detail/component/CbtDescription';
import CbtDetailTabBar from 'app.feature/academy/cbt.detail/component/CbtDetailTabBar';
import ExamApi from 'lib/api/exam';
import { TExamsInfo } from 'app.type/examsInfo';
import { useRecoilValue } from 'recoil';
import { isWebViewState } from 'recoil/Academy/webViewState';

export type TabMenu = 'ROUNDS' | 'COMMUNITY';

const ScreenCbtDetail = () => {
  const router = useRouter();
  const examIdx = Number(router.query.examIdx);
  const isWebView = useRecoilValue(isWebViewState);

  const { data: examInfo = null } = useQuery<TExamsInfo>({
    queryKey: [`/apiV2/exams/${examIdx}`],
    queryFn: () => ExamApi.getExamInfo(examIdx),
  });

  const isCommunityBoard = examInfo?.communityBoardId !== null;
  const communityBoardNum = examInfo?.board?.posts.totalCount || 0;

  const tabsData: TabType[] = [
    { id: 0, name: '시험', size: '50%', value: 'ROUNDS' },
    {
      id: 1,
      name: '시험 정보방 (CBT Q&A)',
      size: '50%',
      value: 'COMMUNITY',
      badge: communityBoardNum,
    },
  ];

  const initialTab =
    tabsData.find((tab) => tab.value === (router.query.tab as TabMenu)) || tabsData[0];
  const [currentTab, setCurrentTab] = useState<TabMenu | undefined>(undefined);

  const handleChangeTab = (selectedTab: TabMenu) => {
    setCurrentTab(selectedTab);
    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, tab: selectedTab },
      },
      undefined,
      { shallow: true }
    );
  };

  useEffect(() => {
    if (router.query.tab && router.query.tab !== currentTab) {
      setCurrentTab(router.query.tab as TabMenu);
    }
  }, [router.query.tab]);

  return (
    <StyledWrapper isWebView={isWebView}>
      <CbtDescription />
      {isCommunityBoard && (
        <>
          <div className="line-tabs-wrapper">
            <LineTabs
              data={tabsData}
              initialTab={initialTab}
              fullWidth
              onChange={handleChangeTab}
            />
          </div>
        </>
      )}
      <div className="mobile-tab-bar">
        <CbtDetailTabBar />
      </div>
      {(currentTab === 'ROUNDS' || currentTab === undefined) && (
        <div className="tab-content">
          <CbtRoundTable />
        </div>
      )}
      {currentTab === 'COMMUNITY' && (
        <div className="tab-content">
          <CommunityBoard />
        </div>
      )}
    </StyledWrapper>
  );
};

export default ScreenCbtDetail;

const StyledWrapper = styled.div<{ isWebView: boolean }>`
  max-width: 1100px;
  margin: 45px auto;
  position: relative;

  .line-tabs-wrapper {
    position: sticky;
    top: ${({ isWebView }) => (isWebView ? '0' : '120px')};
    z-index: 10;
    background-color: white;
    width: 100%;
    max-width: 1100px;
    right: 50%;
  }

  .mobile-tab-bar {
    display: none;
  }

  .tab-content {
    height: auto;
    min-height: 500px;
    padding: 0 20px;
  }

  @media ${({ theme }) => theme.device.tablet} {
    margin: 0 auto 50px;

    .line-tabs-wrapper {
      top: ${({ isWebView }) => (isWebView ? '0' : '50px')};
    }
    .mobile-tab-bar {
      display: block;
    }
  }

  @media ${({ theme }) => theme.device.mobile} {
    margin: 0 auto 50px;

    .line-tabs-wrapper {
      top: ${({ isWebView }) => (isWebView ? '0' : '50px')};
    }
    .mobile-tab-bar {
      display: block;
    }
  }
`;
